import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

export default function EventContent({ searchParams }) {
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [checkAll, setCheckAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // 0: 크림봇 EVENT, 1: 뉴스/소식
  const [selectedCategory, setSelectedCategory] = useState(0);

  // 카테고리 변경 핸들러
  const handleCategoryChange = (category) => {
    console.log('카테고리 변경:', category);
    setSelectedCategory(category);
    setCurrentPage(1); // 카테고리 변경 시 첫 페이지로
    setCheckedItems(new Set()); // 체크박스 초기화
    setCheckAll(false);
  };

  // 데이터 가져오기
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (searchParams?.keyword && currentPage !== 1) {
          setCurrentPage(1);
          return; 
        }

        console.log('=== API 호출 시작 ===');
        console.log('현재 카테고리:', selectedCategory);
        console.log('현재 페이지:', currentPage);
        console.log('검색 파라미터:', searchParams);

        // 파라미터 객체 생성
        const apiParams = {
          curPage: currentPage,
          category: selectedCategory  // 카테고리를 쿼리 파라미터로 추가
        };

        // 검색 파라미터가 있을 때만 추가
        if (searchParams?.keyword && searchParams.keyword.trim()) {
          apiParams.keyword = searchParams.keyword.trim();
          apiParams.filter = searchParams.searchType || 'title';
        }

        console.log('전송할 파라미터:', apiParams);

        // 기존 카테고리별 엔드포인트 사용
        const response = await axios.get(`/api/admin/event/cateId/${selectedCategory}`, {
          params: {
            curPage: currentPage,
            ...(searchParams?.keyword && searchParams.keyword.trim() && {
              keyword: searchParams.keyword.trim(),
              filter: searchParams.searchType || 'title'
            })
          },
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('=== API 응답 ===');
        console.log('응답 상태:', response.status);
        console.log('응답 데이터:', response.data);

        if (response.data && response.data.success !== false) {
          const eventList = response.data.list || [];
          const paginationData = response.data.pagination || {};

          console.log('이벤트 리스트:', eventList);
          console.log('페이지네이션 데이터:', paginationData);

          setEvents(eventList);
          setPagination(paginationData);

          // 관리자 페이지용 페이지네이션 정보 처리
          if (paginationData.totalCount) {
            setTotalCount(paginationData.totalCount);
          }

          // 페이지네이션 계산 로직
          const totalPages = paginationData.totalPages || 1;
          const pageSize = paginationData.pageSize || 10;
          const currentPageForCalc = paginationData.currentPage || currentPage;
          
          // 페이지 그룹 계산 (10페이지씩 그룹화)
          const pageGroup = Math.floor((currentPageForCalc - 1) / 10);
          const startPage = pageGroup * 10 + 1;
          const endPage = Math.min(startPage + 9, totalPages);
          
          // 목록 번호 계산 (역순)
          const startNo = paginationData.totalCount - ((currentPageForCalc - 1) * pageSize);

          const adminPagination = {
            pageCnt: totalPages,
            startPage: startPage,
            endPage: endPage,
            startNo: startNo,
            listCnt: paginationData.totalCount || 0,
            currentPage: currentPageForCalc,
            pageSize: pageSize
          };

          console.log('계산된 페이지네이션:', adminPagination);
          setPagination(adminPagination);

        } else {
          console.error('API 오류:', response.data);
          setError(response.data.error || 'API 호출에 실패했습니다.');
        }
      } catch (error) {
        console.error('=== API 오류 발생 ===');
        console.error('오류 객체:', error);

        let errorMessage = '데이터를 불러오는 중 오류가 발생했습니다.';

        if (error.response) {
          console.error('응답 상태:', error.response.status);
          console.error('응답 데이터:', error.response.data);
          errorMessage = `서버 오류 (${error.response.status}): ${
            error.response.data.message || error.response.statusText
          }`;
        } else if (error.request) {
          console.error('요청 정보:', error.request);
          errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
        } else {
          console.error('오류 메시지:', error.message);
          errorMessage = error.message;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, selectedCategory, searchParams]);

  const handlePageChange = (pageNum) => {
    console.log('페이지 변경:', pageNum);
    setCurrentPage(pageNum);
  };

  const renderPagination = () => {
    if (!pagination || !pagination.pageCnt) {
      return null;
    }

    const pages = [];
    const pageCnt = pagination.pageCnt || 0;
    const startPage = pagination.startPage || 1;
    const endPage = pagination.endPage || 1;

    // 처음으로
    pages.push(
      <li key="first" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage > 1) handlePageChange(1);
          }}
        >
          《
        </a>
      </li>
    );

    // 이전
    pages.push(
      <li key="prev" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage > 1) handlePageChange(currentPage - 1);
          }}
        >
          〈
        </a>
      </li>
    );

    // 페이지 번호들
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={i === currentPage ? 'num_on' : 'num'}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }

    // 다음
    pages.push(
      <li key="next" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage < pageCnt) handlePageChange(currentPage + 1);
          }}
        >
          〉
        </a>
      </li>
    );

    // 마지막으로
    pages.push(
      <li key="last" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage < pageCnt) handlePageChange(pageCnt);
          }}
        >
          》
        </a>
      </li>
    );

    return pages;
  };

  const handleDelete = async () => {
    if (checkedItems.size === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (window.confirm('선택한 항목을 삭제하시겠습니까?')) {
      try {
        const deleteIds = Array.from(checkedItems);

        const response = await axios.post('/api/admin/event/delete', {
          ids: deleteIds,
        });

        if (response.data.success) {
          alert(response.data.message || '선택한 항목이 삭제되었습니다.');
          setCheckedItems(new Set());
          setCheckAll(false);
          // 현재 페이지 데이터 새로고침
          window.location.reload();
        } else {
          alert(response.data.message || '삭제 처리 중 오류가 발생했습니다.');
        }
      } catch (error) {
        console.error('삭제 오류:', error);
        alert(
          '삭제 중 오류가 발생했습니다: ' +
            (error.response?.data?.message || error.message)
        );
      }
    }
  };

  const handleCheckAll = (e) => {
    const isChecked = e.target.checked;
    setCheckAll(isChecked);

    if (isChecked) {
      const allIds = new Set(events.map((event) => event.e_no || event.id));
      setCheckedItems(allIds);
    } else {
      setCheckedItems(new Set());
    }
  };

  const handleCheckItem = (eventId) => {
    const newCheckedItems = new Set(checkedItems);

    if (newCheckedItems.has(eventId)) {
      newCheckedItems.delete(eventId);
    } else {
      newCheckedItems.add(eventId);
    }

    setCheckedItems(newCheckedItems);
    setCheckAll(newCheckedItems.size === events.length && events.length > 0);
  };

  if (loading) {
    return (
      <div className="txt-center" style={{ marginTop: '60px' }}>
        로딩 중...
      </div>
    );
  }

  return (
    <>
      {/* 카테고리 필터 라디오 버튼 */}
      <div className="row-flex-center gap-20">
        <div className="row-flex-center gap-10 mt-2">
          <input
            type="radio"
            value={0}
            id="category1"
            name="eventCategory"
            checked={selectedCategory === 0}
            onChange={() => handleCategoryChange(0)}
          />
          <label htmlFor="category1" className="prt-regular fs-16 pointer">
            크림봇 EVENT
          </label>
        </div>

        <div className="row-flex-center gap-10 mt-2">
          <input
            type="radio"
            value={1}
            id="category2"
            name="eventCategory"
            checked={selectedCategory === 1}
            onChange={() => handleCategoryChange(1)}
          />
          <label htmlFor="category2" className="prt-regular fs-16 pointer">
            뉴스/소식
          </label>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="error-message" style={{ color: 'red', margin: '20px 0' }}>
          {error}
        </div>
      )}

      {/* 데이터 테이블 */}
      <div className="over_hi" style={{ marginTop: '60px' }}>
        <form id="deleteFrm" method="post">
          <table className="adminTable">
            <colgroup>
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '50%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    id="checkall"
                    checked={checkAll}
                    onChange={handleCheckAll}
                  />
                </th>
                <th>No</th>
                <th>카테고리</th>
                <th>제목</th>
                <th>작성자</th>
                <th>작성날짜</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan="6" className="txt-center">
                    {selectedCategory === 0
                      ? '크림봇 EVENT가'
                      : '뉴스/소식이'}{' '}
                    없습니다.
                  </td>
                </tr>
              ) : (
                events.map((event, index) => {
                  const eventId = event.e_no || event.id || event.ID;
                  const eventTitle = event.e_title || event.title || event.TITLE;
                  const eventCategory = event.e_category1 !== undefined ? event.e_category1 : event.category;
                  const eventDate = event.e_add_dt || event.regDate;
                  
                  // 디버깅용 로그
                  console.log(`이벤트 ${index + 1}:`, {
                    eventId,
                    eventTitle,
                    eventCategory,
                    categoryType: typeof eventCategory,
                    eventDate,
                    fullEvent: event
                  });
                  
                  return (
                    <tr key={eventId}>
                      <td>
                        <input
                          type="checkbox"
                          value={eventId}
                          name="check"
                          checked={checkedItems.has(eventId)}
                          onChange={() => handleCheckItem(eventId)}
                        />
                      </td>
                      <td>
                        {pagination.startNo && pagination.startNo > 0
                          ? pagination.startNo - index
                          : (totalCount || 0) - (currentPage - 1) * 10 - index}
                      </td>
                      <td className="txt-center">
                        <span
                          className={`status-badge ${
                            eventCategory === 0 || eventCategory === '0'
                              ? 'status-active'
                              : 'status-ended'
                          }`}
                        >
                          {eventCategory === 0 || eventCategory === '0'
                            ? '크림봇 EVENT'
                            : '뉴스/소식'}
                        </span>
                      </td>
                      <td className="left">
                        <a href={`/admin/event_d/${eventId}`}>
                          {eventTitle}
                        </a>
                      </td>
                      <td className="txt-center">관리자</td>
                      <td className="txt-center">
                        {eventDate ? format(new Date(eventDate), 'yyyy/MM/dd HH:mm') : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </form>
      </div>

      {/* 페이지네이션 */}
      <ul className="txt-center pageing">{renderPagination()}</ul>

      {/* 액션 버튼들 */}
      <div className="row-flex flex-end gap-10">
        <input
          type="button"
          value="삭제"
          className="common-del"
          onClick={handleDelete}
        />
        <input
          type="button"
          value="글쓰기"
          className="common-del"
          onClick={() => (window.location.href = '/admin/event_w')}
        />
      </div>
    </>
  );
}